import {
  AxiosError,
  type AxiosInstance,
  HttpStatusCode,
  isAxiosError,
} from "axios";
import { SendToTrackingService } from "@/api/handlers/errors";
import {
  type RealEstateTitleSearchAPIRequest,
  type RealEstateTitleSearchAPIResponse,
  type RealEstateTitleSearchDetailAPIRequest,
  type RealEstateTitleSearchAPIResponseStatus,
  type RealEstateTitleHasTradeAPIRequest,
  type RealEstateTitleHasTradeAPIResponse,
} from "../types";

const TIMEOUT_MILLISECOND = 60_000;

interface FeedRealEstateTitleAPIResponse {
  data?: RealEstateTitleSearchAPIResponse;
  status: number;
  error?: string;
  timeout?: boolean;
}

interface FeedRealEstateTitleDetailAPIResponse {
  data?: RealEstateTitleSearchAPIResponseStatus;
  status: number;
  error?: string;
}

interface FeedRealEstateTitleHasTradeAPIResponse {
  data?: RealEstateTitleHasTradeAPIResponse;
  status: number;
  error?: string;
}

export const feedAPI = async (
  apiClient: AxiosInstance,
  body: RealEstateTitleSearchAPIRequest
): Promise<FeedRealEstateTitleAPIResponse | undefined> => {
  // タイムアウトの実装
  // タイムアウトした場合、AbortController を使ってリクエスト自体をキャンセルする
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, TIMEOUT_MILLISECOND);

  try {
    const response = await apiClient.post<RealEstateTitleSearchAPIResponse>(
      "/real-estate-title/search",
      body,
      { signal: controller.signal }
    );

    return {
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    if (isAxiosError(error) && error.code === "ERR_CANCELED") {
      return {
        status: HttpStatusCode.Ok,
        timeout: true,
      };
    } else if (
      isAxiosError(error) &&
      (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
    ) {
      SendToTrackingService(error);
    } else if (error instanceof AxiosError && error.response != null) {
      return {
        status: error.response.status ?? HttpStatusCode.InternalServerError,
        error: error.message,
      };
    }

    return {
      status: HttpStatusCode.InternalServerError,
    };
  } finally {
    // 処理終了後は不要なタイマーを片付ける
    clearTimeout(timeoutId);
  }
};

export const feedDetailAPI = async (
  apiClient: AxiosInstance,
  data: RealEstateTitleSearchDetailAPIRequest
): Promise<FeedRealEstateTitleDetailAPIResponse | undefined> => {
  try {
    const response =
      await apiClient.get<RealEstateTitleSearchAPIResponseStatus>(
        `/real-estate-title/${data.id}?real_estate_type=${data.realEstateType}`
      );

    return {
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    if (
      isAxiosError(error) &&
      (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
    ) {
      SendToTrackingService(error);
    }

    if (error instanceof AxiosError && error.response != null) {
      return {
        status: error.response.status ?? HttpStatusCode.InternalServerError,
        error: error.message,
      };
    }

    return {
      status: HttpStatusCode.InternalServerError,
    };
  }
};

export const feedHasTradeAPI = async (
  apiClient: AxiosInstance,
  body: RealEstateTitleHasTradeAPIRequest
): Promise<FeedRealEstateTitleHasTradeAPIResponse | undefined> => {
  try {
    const response = await apiClient.post<RealEstateTitleHasTradeAPIResponse>(
      `/real-estate-title/has-trade`,
      body
    );

    return {
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    if (error instanceof AxiosError && error.response != null) {
      return {
        status: error.response.status ?? HttpStatusCode.InternalServerError,
        error: error.message,
      };
    }

    return {
      status: HttpStatusCode.InternalServerError,
    };
  }
};
